import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Mail, Send } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { api } from '../../lib/api.js';

interface EmailStatus {
  configured: boolean;
  demoMode: boolean;
  host: string | null;
  from: string | null;
  teamsConfigured: boolean;
  teamsFallsBackTo: string;
  missing: string[];
}

interface TestResult {
  delivered: boolean;
  isDemo: boolean;
  detail: string;
}

/**
 * Whether Nova can actually reach anyone by e-mail.
 *
 * Reminders are the whole point of Purreagenten, so «is e-mail set up» is a
 * question that has to be answerable without waiting for something to fall
 * overdue (dokumentasjon/07-utviklingsplan.md 2.1.6).
 */
export function EmailAdmin() {
  const [result, setResult] = useState<TestResult | null>(null);

  const { data: status } = useQuery({
    queryKey: ['email-status'],
    queryFn: () => api.get<EmailStatus>('/system/email'),
  });

  const test = useMutation({
    mutationFn: () => api.post<TestResult>('/system/email/test'),
    onSuccess: setResult,
  });

  const testTeams = useMutation({
    mutationFn: () => api.post<TestResult>('/system/teams/test'),
    onSuccess: setResult,
  });

  if (!status) return null;

  return (
    <GlassPanel>
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-ink-soft" aria-hidden />
        <h2 className="font-display text-ink text-base">E-post</h2>
      </div>

      {status.demoMode ? (
        <p className="text-warning mt-2 flex items-start gap-1.5 text-xs leading-relaxed">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Demo-modus er på. Varsler lagres og vises i Nova, men ingenting sendes ut. Sett{' '}
            <code className="bg-raised rounded px-1">DEMO_MODE=false</code> når dere er klare.
          </span>
        </p>
      ) : status.configured ? (
        <p className="text-success mt-2 flex items-center gap-1.5 text-xs">
          <Check size={13} aria-hidden />
          Satt opp mot {status.host} — sender fra {status.from}
        </p>
      ) : (
        <p className="text-warning mt-2 flex items-start gap-1.5 text-xs leading-relaxed">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Ikke satt opp ennå. Mangler {status.missing.join(' og ')} i <code>.env</code>. Nova sier
            fra i loggen i stedet for å late som varsler går ut.
          </span>
        </p>
      )}

      <p className="text-ink-soft mt-3 text-[11px] leading-relaxed">
        {status.teamsConfigured ? (
          <>
            <span className="text-success">Teams er koblet på</span> (Power Automate-webhook). Varsler
            merket Teams går dit.
          </>
        ) : (
          <>
            Teams er ikke koblet på ennå. Ber en agent om Teams nå, går varselet ut som{' '}
            {status.teamsFallsBackTo === 'EMAIL' ? 'e-post' : 'varsel i Nova'} i stedet.
          </>
        )}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          icon={<Send size={13} aria-hidden />}
          loading={test.isPending}
          onClick={() => test.mutate()}
        >
          Send test til meg selv
        </Button>
        {status.teamsConfigured && (
          <Button
            size="sm"
            icon={<Send size={13} aria-hidden />}
            loading={testTeams.isPending}
            onClick={() => testTeams.mutate()}
          >
            Send Teams-test
          </Button>
        )}
        {result && (
          <span className={result.delivered ? 'text-success text-xs' : 'text-danger text-xs'}>
            {result.detail}
          </span>
        )}
        {test.error && <span className="text-danger text-xs">{test.error.message}</span>}
      </div>
    </GlassPanel>
  );
}
